import { createContext, useContext, useEffect } from "react";

/** Pushes a message into the page's single live region. */
export type AnnounceStatus = (message: string) => void;

/**
 * Defaults to a **no-op**, deliberately.
 *
 * Every tab is also rendered in isolation by its own spec, with no shell above it. A default that
 * threw would force ~10 spec files to wrap themselves in a provider to test something unrelated, and
 * a default that rendered its own region would put two live regions in the page whenever a tab *is*
 * mounted in the shell — which double-announces every transition. Silence when unhosted is the only
 * option that is correct in both cases. `StatusRegion.spec.tsx` covers the hosted path.
 */
export const MyInfoStatusContext = createContext<AnnounceStatus>(
  () => undefined
);

/**
 * Announces `message` in the shell's live region whenever it changes.
 *
 * Called unconditionally at the top of `TabState`, before its early returns — a hook cannot sit
 * behind one. Passing `""` announces nothing, which is how the content state stays quiet.
 */
export const useAnnounceStatus = (message: string) => {
  const announce = useContext(MyInfoStatusContext);

  useEffect(() => {
    if (message) {
      announce(message);
    }
  }, [announce, message]);
};
